"use client";

import React, { useEffect, useState } from "react";
import { ArrowRight, Compass, Cpu, FolderGit2, GitBranch, Languages, Terminal, Trophy } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
interface DestinationNode {
  id: string;
  nodeKey: string;
  enTitle: string;
  viTitle: string;
  enDesc: string;
  viDesc: string;
  to: string;
  actionEn: string;
  actionVi: string;
  tagEn: string;
  tagVi: string;
  coord: string;
  icon: "Cpu" | "GitBranch" | "FolderGit2" | "Trophy";
}
const DESTINATION_NODES: DestinationNode[] = [{
  id: "01",
  nodeKey: "programs",
  enTitle: "Core programs",
  viTitle: "Chương trình đào tạo",
  enDesc: "Structured courses in robotics, coding, and engineering by age track.",
  viDesc: "Khóa học theo lộ trình độ tuổi từ Robotics, Lập trình đến Kỹ thuật chế tạo.",
  to: "/ProgramsPage",
  actionEn: "Explore programs",
  actionVi: "Xem chương trình",
  tagEn: "Ages 4–16",
  tagVi: "Độ tuổi 4–16",
  coord: "LOC.01 // PRG",
  icon: "Cpu"
}, {
  id: "02",
  nodeKey: "curriculum",
  enTitle: "Curriculum roadmap",
  viTitle: "Lộ trình học tập",
  enDesc: "Multi-year spiral learning path designed from foundation to advanced systems.",
  viDesc: "Lộ trình xoắn ốc nhiều năm từ nền tảng đến các hệ thống tự động phức tạp.",
  to: "/LearningPathCurriculumPage",
  actionEn: "Inspect roadmap",
  actionVi: "Xem lộ trình",
  tagEn: "Spiral model",
  tagVi: "Mô hình xoắn ốc",
  coord: "LOC.02 // CUR",
  icon: "GitBranch"
}, {
  id: "03",
  nodeKey: "projects",
  enTitle: "Student projects",
  viTitle: "Dự án học viên",
  enDesc: "Interactive prototypes and student-built machines solving real challenges.",
  viDesc: "Nguyên mẫu tương tác và sản phẩm kỹ thuật do chính học viên thiết kế.",
  to: "#student-projects-gallery",
  actionEn: "Browse gallery",
  actionVi: "Xem dự án",
  tagEn: "Hands-on lab",
  tagVi: "Thực hành tại xưởng",
  coord: "LOC.03 // PRJ",
  icon: "FolderGit2"
}, {
  id: "04",
  nodeKey: "competitions",
  enTitle: "Competitions",
  viTitle: "Đấu trường thi đấu",
  enDesc: "Targeted coaching and national or international robotics tournament prep.",
  viDesc: "Huấn luyện chuyên sâu cho các giải đấu robotics trong nước và quốc tế.",
  to: "#competitions-we-prepare-for",
  actionEn: "View tournaments",
  actionVi: "Xem giải đấu",
  tagEn: "Tournament prep",
  tagVi: "Luyện thi đấu",
  coord: "LOC.04 // CMP",
  icon: "Trophy"
}];
const DEFAULT_MASCOT = {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/ca4d2b98-0711-41d7-bcc7-3111870fb241.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/ca4d2b98-0711-41d7-bcc7-3111870fb241.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/ca4d2b98-0711-41d7-bcc7-3111870fb241.png-wvc-sizes",
  alt: "ROBOTO Academy Mascot"
};
export default function EcosystemBridge() {
  const [lang, setLang] = useState<"en" | "vi">("en");
  const prefersReduced = useReducedMotion();
  useEffect(() => {
    try {
      const stored = localStorage.getItem("roboto_lang");
      if (stored === "vi" || stored === "en") {
        setLang(stored);
      }
    } catch {
      // ignore storage access issues
    }
    const handleLangChange = () => {
      try {
        const current = localStorage.getItem("roboto_lang");
        if (current === "vi" || current === "en") {
          setLang(current);
        }
      } catch {
        // ignore
      }
    };
    window.addEventListener("roboto_lang_change", handleLangChange);
    window.addEventListener("storage", handleLangChange);
    return () => {
      window.removeEventListener("roboto_lang_change", handleLangChange);
      window.removeEventListener("storage", handleLangChange);
    };
  }, []);
  const toggleLanguage = (selected: "en" | "vi") => {
    setLang(selected);
    try {
      localStorage.setItem("roboto_lang", selected);
      window.dispatchEvent(new Event("roboto_lang_change"));
    } catch {
      // ignore
    }
  };
  const renderIcon = (type: DestinationNode["icon"]) => {
    switch (type) {
      case "Cpu":
        return <Cpu className="size-5 text-primary" aria-hidden="true" />;
      case "GitBranch":
        return <GitBranch className="size-5 text-secondary" aria-hidden="true" />;
      case "FolderGit2":
        return <FolderGit2 className="size-5 text-secondary" aria-hidden="true" />;
      case "Trophy":
        return <Trophy className="size-5 text-primary" aria-hidden="true" />;
      default:
        return <Compass className="size-5 text-secondary" aria-hidden="true" />;
    }
  };
  return <section id="ecosystembridge" data-nav="light" className="relative w-full overflow-hidden bg-background py-16 md:py-24 text-foreground border-b border-border">
      {/* Background blueprint grid subtle ornamentation */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.035] bg-[radial-gradient(var(--color-foreground)_1px,transparent_1px)] [background-size:24px_24px]" aria-hidden="true" />

      <div className="container relative mx-auto px-4 md:px-6 max-w-7xl">
        {/* Lab Header Bar */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 pb-8 mb-12 border-b border-border/80">
          <div className="space-y-3 max-w-2xl">
            {/* Precision spec rail */}
            <div className="inline-flex items-center gap-2 rounded-full border border-secondary/30 bg-muted px-3 py-1 text-xs font-mono font-medium text-foreground">
              <span className="size-2 rounded-full bg-secondary animate-pulse" />
              <span>ROBOTO // ECOSYSTEM ROUTER</span>
              <span className="text-muted-foreground">|</span>
              <span className="text-muted-foreground">SYS.VER 4.2</span>
            </div>

            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-foreground">
              {lang === "en" ? "Navigate the learning architecture" : "Hệ thống định vị lộ trình học tập"}
            </h2>

            <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
              {lang === "en" ? "Direct pathways through ROBOTO programs, multi-stage curriculum, hands-on student builds, and competitive leagues." : "Khám phá các mảnh ghép chính: chương trình học, khung lộ trình, sản phẩm thực chiến và đấu trường thi đấu."}
            </p>
          </div>

          {/* Bilingual Language Switcher with Lab Aesthetic */}
          <div className="flex items-center gap-3 self-start md:self-auto shrink-0">
            <div className="flex items-center gap-1.5 rounded-lg border border-border bg-card p-1 shadow-2xs">
              <Languages className="size-4 text-muted-foreground ml-1.5" />
              <Button type="button" variant="ghost" onClick={() => toggleLanguage("en")} className={`h-auto px-2.5 py-1 text-xs font-semibold rounded transition-[color,background-color] ${lang === "en" ? "bg-secondary text-secondary-foreground hover:bg-secondary hover:text-secondary-foreground" : "text-muted-foreground hover:text-foreground"}`} aria-label="Switch to English">
                EN
              </Button>
              <Button type="button" variant="ghost" onClick={() => toggleLanguage("vi")} className={`h-auto px-2.5 py-1 text-xs font-semibold rounded transition-[color,background-color] ${lang === "vi" ? "bg-secondary text-secondary-foreground hover:bg-secondary hover:text-secondary-foreground" : "text-muted-foreground hover:text-foreground"}`} aria-label="Chuyển sang Tiếng Việt">
                VI
              </Button>
            </div>
          </div>
        </div>

        {/* Blueprint connector rail layout */}
        <div className="relative">
          {/* Desktop horizontal blueprint line */}
          <div className="hidden lg:block absolute top-[52px] left-[6%] right-[6%] h-[2px] bg-border border-t border-dashed border-secondary/40 z-0" aria-hidden="true" />

          {/* Mobile/Tablet vertical guide line */}
          <div className="lg:hidden absolute top-8 bottom-8 left-8 w-[2px] bg-border border-l border-dashed border-secondary/40 z-0" aria-hidden="true" />

          {/* 4 Interactive Milestone Nodes */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 relative z-10">
            {DESTINATION_NODES.map((node, i) => <motion.div key={node.id} data-index={i} initial={prefersReduced ? false : {
            opacity: 0,
            y: 16
          }} whileInView={prefersReduced ? undefined : {
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.2
          }} transition={{
            duration: 0.35,
            delay: prefersReduced ? 0 : i * 0.1,
            ease: [0.16, 1, 0.3, 1]
          }} className="flex flex-col">
                <Card className="group relative flex-1 flex flex-col justify-between border-border bg-card shadow-xs transition-[border-color,box-shadow,transform] duration-200 hover:-translate-y-1 hover:border-secondary hover:shadow-md">
                  {/* Blueprint corner notch accent */}
                  <div className="absolute top-0 right-0 w-3 h-3 border-t-2 border-r-2 border-secondary/60 rounded-tr-sm pointer-events-none" aria-hidden="true" />

                  <div>
                    <CardHeader className="gap-3 pb-3">
                      {/* Top milestone indicator & node ID */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="size-8 rounded-lg bg-muted flex items-center justify-center border border-border group-hover:border-secondary/50 transition-[border-color]">
                            {renderIcon(node.icon)}
                          </div>
                          <span className="font-mono text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                            NODE {node.id}
                          </span>
                        </div>
                        <Badge variant="outline" className="font-mono text-[10px] tracking-tight border-border/80 text-muted-foreground">
                          {lang === "en" ? node.tagEn : node.tagVi}
                        </Badge>
                      </div>

                      <div className="mt-1">
                        <div className="text-[10px] font-mono text-secondary mb-1 flex items-center gap-1">
                          <Terminal className="size-2.5" />
                          <span>{node.coord}</span>
                        </div>
                        <CardTitle className="text-lg font-semibold tracking-tight text-foreground group-hover:text-primary transition-[color]">
                          {lang === "en" ? node.enTitle : node.viTitle}
                        </CardTitle>
                      </div>
                    </CardHeader>

                    <CardContent className="pt-0">
                      <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                        {lang === "en" ? node.enDesc : node.viDesc}
                      </p>
                    </CardContent>
                  </div>

                  <CardFooter className="pt-3 border-t border-border/50">
                    <Button asChild variant="ghost" size="sm" className="w-full justify-between text-xs font-medium px-2 text-foreground group-hover:text-primary group-hover:bg-muted/80">
                      <Link to={node.to}>
                        <span>{lang === "en" ? node.actionEn : node.actionVi}</span>
                        <ArrowRight className="size-3.5 transition-transform duration-200 group-hover:translate-x-1 text-secondary" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>)}
          </div>
        </div>

        {/* Lab Footer Metadata Rail with right clearance for mascot */}
        <div className="mt-12 pt-6 border-t border-border/60 flex flex-wrap items-center justify-between gap-4 text-xs font-mono text-muted-foreground pr-20 sm:pr-24 md:pr-28 lg:pr-32">
          <div className="flex items-center gap-3">
            <span className="size-1.5 rounded-full bg-secondary" />
            <span>ACADEMY DIRECTORY: 4 ACTIVE PATHWAYS</span>
          </div>
          <div className="flex items-center gap-4">
            <span>EXPEDITION LEVEL: FOUNDATION → ELITE TOURNAMENT</span>
          </div>
        </div>
      </div>

      {/* Floating Decorative Mascot (Static) */}
      <div aria-hidden="true" className="pointer-events-none select-none absolute bottom-3 right-3 sm:bottom-4 sm:right-6 md:bottom-6 md:right-8 z-0">
        <img src={DEFAULT_MASCOT.src} data-wvc-srcset={DEFAULT_MASCOT.srcset} data-wvc-sizes={DEFAULT_MASCOT.sizes} alt="" loading="lazy" className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 lg:w-28 lg:h-28 object-contain bg-transparent drop-shadow-md pointer-events-none select-none" />
      </div>
    </section>;
}